import type { ReactNode } from 'react';
import { ArrowRight, Ban, CalendarPlus, CircleCheck, CircleDashed, CircleOff, Clock, Flag, MoveRight, RefreshCw, UserPen } from 'lucide-react';
import type { PrepIcon, PrepTone, TimelineRow } from './prepText.ts';

const ICON: Record<PrepIcon, typeof Clock> = {
  planned: Clock,
  done: CircleCheck,
  manual: UserPen,
  removed: CircleOff,
  syncing: RefreshCw,
  past: CircleDashed,
  create: CalendarPlus,
  move: MoveRight,
  keep: Clock,
  impossible: Ban,
};

const TONE: Record<PrepTone, string> = {
  accent: 'text-accent',
  ok: 'text-ok',
  info: 'text-info',
  warn: 'text-warn',
  risk: 'text-risk',
  muted: 'text-muted',
};

/** Tillstånd i text + ikon (färg är aldrig enda signal). */
export function PrepStatusChip({ text, tone, icon }: { text: string; tone: PrepTone; icon: PrepIcon }) {
  const I = ICON[icon];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${TONE[tone]}`}>
      <I size={14} aria-hidden className="shrink-0" />
      {text}
    </span>
  );
}

/**
 * Kompakt, vertikal tidslinje: steg i tidsordning och deadlinen sist. Radbryts i stället för att spilla
 * horisontellt (360 px, 200 % zoom): inga fasta bredder, min-w-0. Varje steg har en hel mening för
 * skärmläsare; den visuella korta raden är dold för dem så att inget läses två gånger.
 */
export function PlanTimeline({ rows, deadline, label, actions }: { rows: TimelineRow[]; deadline: { when: string; sentence: string }; label: string; actions?: (row: TimelineRow) => ReactNode }) {
  return (
    <ol className="autoday-prep-timeline" aria-label={label}>
      {rows.map((r) => {
        const extra = actions?.(r);
        return (
          <li key={r.key} className="autoday-prep-step" data-tone={r.status.tone} data-muted={r.muted || undefined}>
            <span className="autoday-prep-marker" aria-hidden>
              {r.n}
            </span>
            <div className="min-w-0 flex-1 pb-1">
              <span className="sr-only">{r.sentence}</span>
              <div aria-hidden className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className={`min-w-0 font-medium [overflow-wrap:anywhere] ${r.muted ? 'text-muted' : 'text-text'}`}>{r.title}</span>
                <PrepStatusChip {...r.status} />
              </div>
              <p aria-hidden className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
                {r.from && (
                  <>
                    <span className="line-through">{r.from}</span>
                    <ArrowRight size={12} className="shrink-0" />
                  </>
                )}
                {r.when && <span className={r.from ? 'font-medium text-text' : ''}>{r.when}</span>}
                {r.when && <span>·</span>}
                <span>{r.duration}</span>
              </p>
              {r.note && (
                <p aria-hidden className={`mt-0.5 text-xs ${TONE[r.noteTone]}`}>
                  {r.note}
                </p>
              )}
              {extra && <div className="mt-1.5 flex flex-wrap items-center gap-2">{extra}</div>}
            </div>
          </li>
        );
      })}
      <li className="autoday-prep-step autoday-prep-deadline">
        <span className="autoday-prep-marker" aria-hidden>
          <Flag size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="sr-only">{deadline.sentence}</span>
          <p aria-hidden className="text-sm font-semibold">
            Deadline <span className="font-normal text-muted">· {deadline.when}</span>
          </p>
        </div>
      </li>
    </ol>
  );
}
